import { nanoid } from '@reduxjs/toolkit';
import {
  TemplateState,
  PassTemplateApiResponse,
  LocationUIState,
  BeaconUIState,
  FieldsState,
  GetPassTemplateResponse,
  DynamicFormIncluded,
  AppleEnhancedDesignState,
  AppleEnhancedDesignFeaturedActions,
  FeaturedActionSlotKey,
  FeaturedActionType,
  Image,
  WatermarkSettings,
} from '../../../../store/types/template';
import {
  FormState,
  QueryParamState,
  FormFieldState,
  FormFieldOptionState,
} from '../../../../store/types/form';
import { initialFormState } from '../../../../store/redux/slices/form.slice';
import { buildInitialFields } from './fieldHelpers';
import { FIELD_CONFIG, SLOT_ORDER } from './fieldVisibilityConfig';
import {
  FEATURED_ACTION_SLOT_KEYS,
  createEmptyFeaturedAction,
} from './featuredActionTypes';

/** Map API template types to FIELD_CONFIG keys. */
const normalizeTemplateType = (apiType: string): keyof typeof FIELD_CONFIG => {
  if (apiType === 'storeCard') return 'loyalty';
  if (apiType in FIELD_CONFIG) return apiType as keyof typeof FIELD_CONFIG;
  return 'loyalty';
};
import {
  STAMP_WORDS,
  type StampWord,
  getStampImageKey,
  getStampShortValueKey,
  getStampLongValueKey,
} from './stampCardConstants';

const FIELD_SECTIONS_FOR_STAMP: (keyof FieldsState)[] = [
  'header',
  'central',
  'details',
  'back',
];

function resolveStampKeyToSectionSlot(
  fields: FieldsState | undefined,
  apiValue: string
): string {
  if (!apiValue || !fields) return apiValue;
  const trimmed = apiValue.trim();
  if (!trimmed) return apiValue;
  for (const section of FIELD_SECTIONS_FOR_STAMP) {
    const sectionFields = fields[section];
    if (!sectionFields || typeof sectionFields !== 'object') continue;
    for (const [slot, field] of Object.entries(sectionFields)) {
      if (!field || typeof field !== 'object') continue;
      const id = (field as { id?: string }).id;
      const label = (field as { label?: string }).label;
      if (id && id === trimmed) return `${section}_${slot}`;
      if (label && String(label).trim().toLowerCase() === trimmed.toLowerCase())
        return `${section}_${slot}`;
    }
  }
  return apiValue;
}

const getDynamicFormFromIncluded = (
  included: GetPassTemplateResponse['included']
): DynamicFormIncluded | undefined =>
  included?.find((item) => item.type === 'dynamicFormTemplate');

const getDynamicFormFromAttributes = (
  attributes: PassTemplateApiResponse['data']['attributes'] | undefined
): FormState | undefined => {
  if (!attributes || typeof attributes !== 'object') return undefined;
  const raw =
    (attributes as Record<string, unknown>).dynamicForm ??
    (attributes as Record<string, unknown>).dynamic_form;
  return raw as FormState | undefined;
};

const mapFormFieldsFromApi = (
  rawFormFields: DynamicFormIncluded['attributes']['formFields']
): FormState['formFields'] => {
  if (!Array.isArray(rawFormFields)) return initialFormState.formFields;
  return rawFormFields.map(
    (
      f: NonNullable<
        DynamicFormIncluded['attributes']['formFields']
      >[number] & { date_format?: string }
    ) => {
      const rawDateFormat = f.dateFormat ?? f.date_format;
      const dateFormat: FormFieldState['dateFormat'] | undefined =
        (rawDateFormat as FormFieldState['dateFormat']) ??
        (f.type === 'date' ? '%d/%m/%Y' : undefined);
      return {
        _clientId: nanoid(),
        extId: f.extId ?? false,
        id: f.id ?? '',
        label: f.label ?? '',
        name: f.name ?? '',
        required: f.required ?? false,
        type: (f.type as FormFieldState['type']) ?? 'input',
        validation: f.validation
          ? {
              name: f.validation.name ?? 'none',
              pattern: f.validation.pattern ?? '^.*$',
              message: f.validation.message ?? '',
            }
          : undefined,
        options: f.options?.map((opt) => ({
          id: opt.id ?? nanoid(),
          label: opt.label ?? '',
        })),
        placeholder: f.placeholder,
        ...(dateFormat !== undefined && { dateFormat }),
      };
    }
  );
};

const mapFeaturedActionsFromApi = (
  featuredActions:
    | Record<
        FeaturedActionSlotKey,
        { type: FeaturedActionType | null; url?: string } | null
      >
    | undefined
    | null
): AppleEnhancedDesignFeaturedActions => {
  return FEATURED_ACTION_SLOT_KEYS.reduce((acc, slot) => {
    const value = featuredActions?.[slot];
    acc[slot] = value
      ? { type: value.type ?? null, url: value.url ?? '' }
      : createEmptyFeaturedAction();
    return acc;
  }, {} as AppleEnhancedDesignFeaturedActions);
};

const mapAppleEnhancedDesignFromApi = (
  attributes: PassTemplateApiResponse['data']['attributes']
): AppleEnhancedDesignState => {
  const design = attributes.appleEnhancedDesign;
  const images = design?.images ?? {};
  const artwork = images.artwork ?? '';
  const secondaryLogo = images.secondaryLogo ?? '';
  const venueMap = images.venueMap ?? '';
  const artworkBrightness = design?.artworkBrightness ?? null;
  const type = design?.type ?? null;
  const featuredActions = mapFeaturedActionsFromApi(design?.featuredActions);
  return {
    type,
    images: { artwork, secondaryLogo, venueMap },
    artworkBrightness,
    featuredActions,
  };
};

export const mapApiResponseToFormState = (
  responseOrTemplateData:
    | GetPassTemplateResponse
    | PassTemplateApiResponse['data']
): FormState => {
  const hasIncluded = 'included' in responseOrTemplateData;
  const included = hasIncluded
    ? (responseOrTemplateData as GetPassTemplateResponse).included
    : undefined;
  const templateData = hasIncluded
    ? (responseOrTemplateData as GetPassTemplateResponse).data
    : (responseOrTemplateData as PassTemplateApiResponse['data']);

  const dynamicFormFromIncluded = getDynamicFormFromIncluded(included);
  if (dynamicFormFromIncluded) {
    const attrs = dynamicFormFromIncluded.attributes;
    const rawQueryParams = attrs.queryParams ?? [];
    const queryParams: QueryParamState[] = rawQueryParams.map((q) => ({
      _clientId: nanoid(),
      id: q.id ?? '',
      name: q.name ?? '',
      required: q.required ?? false,
    }));
    const formFields = mapFormFieldsFromApi(attrs.formFields);

    return {
      ...initialFormState,
      enabled: attrs.enabled ?? false,
      slug: attrs.slug ?? '',
      backgroundColor:
        attrs.backgroundColor ?? initialFormState.backgroundColor,
      componentFillColor:
        attrs.componentFillColor ?? initialFormState.componentFillColor,
      componentFontColor:
        attrs.componentFontColor ?? initialFormState.componentFontColor,
      buttonText: attrs.buttonCtaText ?? initialFormState.buttonText,
      buttonColor: attrs.buttonColor ?? initialFormState.buttonColor,
      buttonCtaColor: attrs.buttonCtaColor ?? initialFormState.buttonCtaColor,
      formLogoImage: attrs.logoImageUrl ?? initialFormState.formLogoImage,
      formFaviconImage: attrs.faviconUrl ?? initialFormState.formFaviconImage,
      footerEnabled: attrs.footerEnabled ?? initialFormState.footerEnabled,
      footerContent: attrs.footerContent ?? initialFormState.footerContent,
      footerColor: attrs.footerColor ?? initialFormState.footerColor,
      queryParams,
      formFields,
      dataProcessingConsentText:
        attrs.dataProcessingConsentText ??
        initialFormState.dataProcessingConsentText,
      dataProcessingConsentRequired:
        attrs.dataProcessingConsentRequired ??
        initialFormState.dataProcessingConsentRequired,
    };
  }

  if (!templateData?.attributes) return initialFormState;

  const raw = getDynamicFormFromAttributes(
    templateData.attributes as PassTemplateApiResponse['data']['attributes']
  );
  if (!raw) return initialFormState;

  const rawQueryParams =
    (raw as FormState & { query_params?: QueryParamState[] }).queryParams ??
    (raw as FormState & { query_params?: QueryParamState[] }).query_params ??
    [];
  const queryParams: QueryParamState[] = rawQueryParams.map(
    (q: Partial<QueryParamState>) => ({
      _clientId: q._clientId ?? nanoid(),
      id: q.id ?? '',
      name: q.name ?? '',
      required: q.required ?? false,
    })
  );

  const rawFormFields =
    (raw as FormState & { form_fields?: FormFieldState[] }).formFields ??
    (raw as FormState & { form_fields?: FormFieldState[] }).form_fields ??
    [];
  const formFields: FormState['formFields'] = Array.isArray(rawFormFields)
    ? rawFormFields.map(
        (f: Partial<FormFieldState> & { date_format?: string }) => {
          const rawDateFormat =
            f.dateFormat ?? (f as { date_format?: string }).date_format;
          const dateFormat: FormFieldState['dateFormat'] | undefined =
            (rawDateFormat as FormFieldState['dateFormat']) ??
            (f.type === 'date' ? '%d/%m/%Y' : undefined);
          return {
            _clientId: f._clientId ?? nanoid(),
            extId: f.extId ?? false,
            id: f.id ?? '',
            label: f.label ?? '',
            name: f.name ?? '',
            required: f.required ?? false,
            type: f.type,
            validation: f.validation,
            options: f.options?.map((opt: Partial<FormFieldOptionState>) => ({
              id: opt.id ?? nanoid(),
              label: opt.label ?? '',
            })),
            placeholder: f.placeholder,
            ...(dateFormat !== undefined && { dateFormat }),
          };
        }
      )
    : initialFormState.formFields;

  return {
    ...initialFormState,
    ...raw,
    queryParams,
    formFields,
  };
};

export const buildOriginalDefaultValues = (
  fields: FieldsState | undefined
): FieldsState => {
  const result: FieldsState = {
    header: {},
    central: {},
    details: {},
    back: {},
  };
  if (!fields) return result;
  (['header', 'central', 'details', 'back'] as const).forEach((section) => {
    Object.keys(fields[section] || {}).forEach((slot) => {
      const field = fields[section][slot];
      if (field) {
        result[section][slot] = {
          id: field.id || '',
          label: field.label || '',
          defaultValue: field.defaultValue || '',
        };
      }
    });
  });
  return result;
};

const pickWatermark = (image?: Image): WatermarkSettings => ({
  color: image?.watermarkColor ?? '',
  position: image?.watermarkPosition ?? '',
});

export const mapApiResponseToTemplateState = (
  templateData: PassTemplateApiResponse['data']
): Partial<TemplateState> => {
  const attrs = templateData.attributes as Record<string, unknown>;
  const stampCardRaw = attrs.stampCard ?? attrs.stamp_card;
  const stampCard = stampCardRaw as
    | {
        stampCount?: number;
        stampShortKey?: string;
        stampLongKey?: string;
        updateBannerOnStamp?: boolean;
        stamps?: Record<
          string,
          {
            message?: string | null;
            shortValue?: string | null;
            longValue?: string | null;
            image?: string | null;
            watermark?: { color?: string | null; position?: string | null };
          }
        >;
      }
    | undefined;

  const stampCount =
    templateData.attributes.templateType === 'stampCard' && stampCard
      ? Math.min(Math.max(Number(stampCard.stampCount) ?? 2, 2), 12)
      : undefined;
  const apiStampShortKey =
    templateData.attributes.templateType === 'stampCard' && stampCard
      ? (stampCard.stampShortKey ?? '')
      : undefined;
  const apiStampLongKey =
    templateData.attributes.templateType === 'stampCard' && stampCard
      ? (stampCard.stampLongKey ?? '')
      : undefined;
  const fieldsFromApi =
    (templateData.attributes.fields as FieldsState) || undefined;
  const stampShortKey =
    apiStampShortKey !== undefined
      ? resolveStampKeyToSectionSlot(fieldsFromApi, apiStampShortKey)
      : undefined;
  const stampLongKey =
    apiStampLongKey !== undefined
      ? resolveStampKeyToSectionSlot(fieldsFromApi, apiStampLongKey)
      : undefined;

  let notificationMessages: TemplateState['notificationMessages'] | undefined;
  let stampShortValues: TemplateState['stampShortValues'] | undefined;
  let stampLongValues: TemplateState['stampLongValues'] | undefined;
  let stampFeedbackTypes: TemplateState['stampFeedbackTypes'] | undefined;
  if (
    templateData.attributes.templateType === 'stampCard' &&
    stampCard?.stamps
  ) {
    const count = stampCount ?? 2;
    const requiredCount = Math.min(count + 1, 13);
    notificationMessages = [];
    const shortValues: Record<string, string> = {};
    const longValues: Record<string, string> = {};
    let hasAnyNotificationMessage = false;
    let hasAnyStampImage = false;
    let hasAnyPassText = false;
    for (let i = 0; i < requiredCount; i++) {
      const word = STAMP_WORDS[i] as StampWord;
      const stampData = stampCard.stamps[word];
      const message = stampData?.message ?? '';
      const shortVal = stampData?.shortValue ?? '';
      const longVal = stampData?.longValue ?? '';
      const image = stampData?.image ?? '';
      if (message != null && String(message).trim() !== '') {
        hasAnyNotificationMessage = true;
      }
      if (image != null && String(image).trim() !== '') {
        hasAnyStampImage = true;
      }
      if (
        (shortVal != null && String(shortVal).trim() !== '') ||
        (longVal != null && String(longVal).trim() !== '')
      ) {
        hasAnyPassText = true;
      }
      notificationMessages.push({
        id: nanoid(),
        label: message,
        slot: String(i),
      });
      shortValues[getStampShortValueKey(word)] = shortVal;
      longValues[getStampLongValueKey(word)] = longVal;
    }
    stampShortValues = shortValues;
    stampLongValues = longValues;
    const feedbackTypes: TemplateState['stampFeedbackTypes'] = [];
    if (hasAnyNotificationMessage) {
      feedbackTypes.push('sendPushNotification');
    }
    const updateBannerOnStampFromApi = stampCard?.updateBannerOnStamp === true;
    if (updateBannerOnStampFromApi) {
      feedbackTypes.push('changePassBanner');
    }
    if (hasAnyPassText) {
      feedbackTypes.push('updatePassText');
    }
    stampFeedbackTypes = feedbackTypes;
  }

  const appleEnhancedDesign = mapAppleEnhancedDesignFromApi(
    templateData.attributes
  );

  const templateState: Partial<TemplateState> = {
    uuid: templateData.id,
    status: (templateData.attributes as { status?: TemplateState['status'] })
      .status,
    templateName: templateData.attributes.name,
    templateType: normalizeTemplateType(templateData.attributes.templateType),
    defaultLanguage: templateData.attributes.defaultLanguage || 'en',
    transitType: templateData.attributes.transitType,
    description: templateData.attributes.description,
    centralTitle: templateData.attributes.centralTitle,
    notificationHeader: templateData.attributes.notificationHeader,
    chosenGoogleLogo: templateData.attributes.chosenGoogleLogo === 'wide_logo' ? 'wide_logo' : 'icon',
    focusedField: null,
    images: (() => {
      const sharedBanner = templateData.attributes.images?.banner;
      const googleBanner = templateData.attributes.images?.google?.banner;
      const appleBanner = templateData.attributes.images?.apple?.banner;
      const sharedWatermarkColor =
        templateData.attributes.images?.watermark?.color || '';
      const sharedWatermarkPosition =
        templateData.attributes.images?.watermark?.position || '';

      const finalSharedBanner = sharedBanner;
      const finalAppleBanner = appleBanner;
      const finalAppleWatermarkColor =
        templateData.attributes.images?.apple?.watermark?.color || '';
      const finalAppleWatermarkPosition =
        templateData.attributes.images?.apple?.watermark?.position || '';

      const sharedBase = {
        logo: templateData.attributes.images?.logo,
        icon: templateData.attributes.images?.icon,
        banner: finalSharedBanner,
        watermarkColor: sharedWatermarkColor,
        watermarkPosition: sharedWatermarkPosition,
      };

      const sharedStampImages: Record<string, string> = {};
      let stampWatermarkColor = sharedWatermarkColor;
      let stampWatermarkPosition = sharedWatermarkPosition;
      if (
        templateData.attributes.templateType === 'stampCard' &&
        stampCard?.stamps
      ) {
        const stamps = stampCard.stamps;
        const stampZeroWatermark = stamps.zero?.watermark;
        if (
          stampZeroWatermark?.color != null &&
          stampZeroWatermark.color !== ''
        ) {
          stampWatermarkColor = stampZeroWatermark.color;
        }
        if (
          stampZeroWatermark?.position != null &&
          stampZeroWatermark.position !== ''
        ) {
          stampWatermarkPosition = stampZeroWatermark.position;
        }
        for (const word of STAMP_WORDS) {
          const stampData = stamps[word];
          if (stampData?.image != null && stampData.image !== '') {
            const key = getStampImageKey(word as StampWord);
            sharedStampImages[key] = stampData.image;
          }
        }
      }

      return {
        shared: {
          ...sharedBase,
          ...(Object.keys(sharedStampImages).length > 0
            ? sharedStampImages
            : {}),
          ...(templateData.attributes.templateType === 'stampCard' &&
          stampCard?.stamps
            ? {
                watermarkColor: stampWatermarkColor,
                watermarkPosition: stampWatermarkPosition,
              }
            : {}),
        },
        apple: {
          logo: templateData.attributes.images?.apple?.logo,
          icon: templateData.attributes.images?.apple?.icon,
          banner: finalAppleBanner,
          thumbnail: templateData.attributes.images?.apple?.thumbnail,
          watermarkColor: finalAppleWatermarkColor,
          watermarkPosition: finalAppleWatermarkPosition,
        },
        google: {
          logo: templateData.attributes.images?.google?.logo,
          wideLogo: templateData.attributes.images?.google?.wideLogo,
          banner: googleBanner,
          watermarkColor:
            templateData.attributes.images?.google?.watermark?.color || '',
          watermarkPosition:
            templateData.attributes.images?.google?.watermark?.position || '',
        },
      };
    })(),
    colors: {
      passBackgroundColor: templateData.attributes.colors?.background || '',
      titleFontColor: templateData.attributes.colors?.label || '',
      bodyFontColor: templateData.attributes.colors?.text || '',
    },
    fields: (() => {
      const f = templateData.attributes.fields as FieldsState | undefined;
      if (!f) {
        return buildInitialFields(
          templateData.attributes.templateType || 'loyalty'
        );
      }

      const sortSection = (section: Record<string, any>) => {
        if (!section) return {};
        return Object.fromEntries(
          SLOT_ORDER.filter((slot) => slot in section).map((slot) => [
            slot,
            section[slot],
          ])
        );
      };

      return {
        header: sortSection(f.header),
        central: sortSection(f.central),
        details: sortSection(f.details),
        back: sortSection(f.back),
      };
    })(),
    nfc: {
      enabled: templateData.attributes.nfc?.enabled || false,
      source: templateData.attributes.nfc?.source || 'uuid',
      customValue: templateData.attributes.nfc?.customValue || '',
      googleNfc:
        typeof templateData.attributes.googleNfcEnabled === 'boolean'
          ? templateData.attributes.googleNfcEnabled
          : (templateData.attributes.googleNfcEnabled?.enabled ?? false),
    },
    _originalNfc: {
      enabled: templateData.attributes.nfc?.enabled || false,
      source: templateData.attributes.nfc?.source || 'uuid',
      customValue: templateData.attributes.nfc?.customValue || '',
      googleNfc:
        typeof templateData.attributes.googleNfcEnabled === 'boolean'
          ? templateData.attributes.googleNfcEnabled
          : (templateData.attributes.googleNfcEnabled?.enabled ?? false),
    },
    barcode: {
      enabled: templateData.attributes.barcode?.enabled || false,
      source: templateData.attributes.barcode?.source || 'uuid',
      type: templateData.attributes.barcode?.type || 'qr',
      value: templateData.attributes.barcode?.value || '',
      displayText: templateData.attributes.barcode?.displayText || false,
    },
    _originalBarcode: {
      enabled: templateData.attributes.barcode?.enabled || false,
      source: templateData.attributes.barcode?.source || 'uuid',
      type: templateData.attributes.barcode?.type || 'qr',
      value: templateData.attributes.barcode?.value || '',
      displayText: templateData.attributes.barcode?.displayText || false,
    },
    appLinking: (() => {
      const al = templateData.attributes.appLinking;
      const rawDisplayText = al?.google?.displayText as unknown;
      const googleDisplayText =
        rawDisplayText && typeof rawDisplayText === 'object'
          ? ((rawDisplayText as { text?: string }).text ?? '')
          : ((rawDisplayText as string | undefined) ?? '');
      const googleApp = al?.google?.app as
        | {
            playStoreUrl?: string;
            packageName?: string;
            play_store_url?: string;
            package_name?: string;
          }
        | undefined;
      return {
        appleAppStoreUrl: al?.apple?.appStoreUrl || [],
        googleDisplayText,
        googleAppPackageName:
          googleApp?.packageName || googleApp?.package_name || '',
        googleAppPlayStoreUrl:
          googleApp?.playStoreUrl || googleApp?.play_store_url || '',
        googleWebUrl: al?.google?.web?.url || '',
      };
    })(),
    loyaltyBalance: {
      enabled: templateData.attributes.loyaltyBalance?.enabled || false,
      numberType:
        (templateData.attributes.loyaltyBalance?.numberType as
          | 'integer'
          | 'decimal') || 'integer',
    },
    locations: (templateData.attributes.locations || []).map(
      (loc): LocationUIState => ({
        ...loc,
        _id: nanoid(),
      })
    ),
    beacons: (templateData.attributes.beacons || []).map(
      (beacon): BeaconUIState => ({
        ...beacon,
        _id: nanoid(),
      })
    ),
    appleEnhancedDesign,
    ...(stampCount != null && { stampCount }),
    ...(stampShortKey !== undefined && { stampShortKey }),
    ...(stampLongKey !== undefined && { stampLongKey }),
    ...(notificationMessages != null && { notificationMessages }),
    ...(stampShortValues != null && { stampShortValues }),
    ...(stampLongValues != null && { stampLongValues }),
    ...(templateData.attributes.templateType === 'stampCard' && {
      stampFeedbackTypes: stampFeedbackTypes ?? [],
      updateBannerOnStamp:
        stampCard != null ? stampCard.updateBannerOnStamp === true : false,
    }),
    _appleEnhancedDesignOnLoad: {
      type: appleEnhancedDesign.type,
      images: { ...appleEnhancedDesign.images },
      featuredActions: {
        one: { ...appleEnhancedDesign.featuredActions.one },
        two: { ...appleEnhancedDesign.featuredActions.two },
      },
    },
    _overrideNfc: false,
    _overrideBarcode: false,
    _overrideFeaturedActions: false,
  };

  return {
    ...templateState,
    _originalWatermarks: {
      shared: pickWatermark(templateState.images?.shared),
      apple: pickWatermark(templateState.images?.apple),
      google: pickWatermark(templateState.images?.google),
    },
  };
};
